/** @jsxImportSource @emotion/react */
import React, { useState, useCallback, useRef } from 'react'
import { css } from '@emotion/react'
import { Modal, App } from 'antd'
import {
  UploadOutlined,
  DownloadOutlined,
  InboxOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
} from '@ant-design/icons'
import colors from '@sportninja/common/constants/appColors'
import req from '@sportninja/common/api/request'

interface ConcreteSlotImportModalProps {
  isOpen: boolean
  onClose: () => void
  competitionId: string
  flightId?: string | null
  onImportSuccess: () => void
}

const ConcreteSlotImportModal: React.FC<ConcreteSlotImportModalProps> = ({
  isOpen,
  onClose,
  competitionId,
  flightId,
  onImportSuccess,
}) => {
  const { message } = App.useApp()
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [file, setFile] = useState<File | null>(null)
  const [isDragOver, setIsDragOver] = useState(false)
  const [loading, setLoading] = useState(false)
  const [templateLoading, setTemplateLoading] = useState(false)
  const [result, setResult] = useState<{
    type: 'success' | 'error'
    title: string
    message: string
    errors?: Array<Record<string, string>>
  } | null>(null)

  const handleReset = useCallback(() => {
    setFile(null)
    setResult(null)
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }, [])

  const handleClose = useCallback(() => {
    handleReset()
    onClose()
  }, [handleReset, onClose])

  const handleTemplateDownload = useCallback(async () => {
    setTemplateLoading(true)
    try {
      const response = await req(
        `/schedules/${competitionId}/concrete-slots/template`,
        { method: 'GET', parseJSON: false }
      )
      const blob = await response.blob()
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = 'time_slots_import_template.csv'
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      window.URL.revokeObjectURL(url)
    } catch {
      message.error('Failed to download template')
    } finally {
      setTemplateLoading(false)
    }
  }, [competitionId, message])

  const handleFileSelect = useCallback(
    (selectedFile: File) => {
      const isValid =
        selectedFile.name.endsWith('.csv') || selectedFile.type === 'text/csv'
      if (!isValid) {
        message.error('Please select a CSV file')
        return
      }
      setFile(selectedFile)
      setResult(null)
    },
    [message]
  )

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault()
      setIsDragOver(false)
      const droppedFile = e.dataTransfer.files?.[0]
      if (droppedFile) {
        handleFileSelect(droppedFile)
      }
    },
    [handleFileSelect]
  )

  const handleUpload = useCallback(async () => {
    if (!file) {
      return
    }

    setLoading(true)
    setResult(null)

    try {
      const formData = new FormData()
      formData.append('file', file)
      formData.append('conflicts', 'ignore')
      if (flightId) {
        formData.append('flight_id', flightId)
      }

      const response = await req(
        `/schedules/${competitionId}/concrete-slots/import/csv`,
        {
          method: 'POST',
          body: formData,
          skipAutoContentType: true,
        }
      )

      const data = response?.data || response
      const successCount = data?.success?.count || 0
      const failedCount = data?.failed?.count || 0
      const errors = data?.failed?.errors || []

      if (failedCount > 0) {
        setResult({
          type: 'error',
          title: 'Import completed with errors',
          message: `${successCount} slots imported, ${failedCount} rows had errors`,
          errors,
        })
        if (successCount > 0) {
          onImportSuccess()
        }
      } else {
        setResult({
          type: 'success',
          title: 'Import successful',
          message: `${successCount} game slots imported`,
        })
        onImportSuccess()
      }
    } catch (err: unknown) {
      // The API returns 409 with error details — throwOnHTTPError puts
      // the parsed JSON directly on the error object (no .data wrapper)
      const apiError = err as Record<string, unknown>
      const errorData = (apiError?.data || apiError) as {
        failed?: { count?: number; errors?: Array<Record<string, string>> }
        success?: { count?: number }
      }
      const failedCount = errorData?.failed?.count || 0
      const successCount = errorData?.success?.count || 0
      const errors = errorData?.failed?.errors || []

      if (failedCount > 0) {
        setResult({
          type: 'error',
          title: 'Import failed',
          message: `${failedCount} rows had errors. ${successCount} slots imported.`,
          errors,
        })
      } else {
        setResult({
          type: 'error',
          title: 'Import failed',
          message:
            err instanceof Error ? err.message : 'An unexpected error occurred',
        })
      }
    } finally {
      setLoading(false)
    }
  }, [file, competitionId, flightId, onImportSuccess])

  return (
    <Modal
      open={isOpen}
      onCancel={handleClose}
      footer={null}
      title={null}
      closable
      width={600}
      styles={{
        content: {
          backgroundColor: colors.SECONDARY_400,
          border: `1px solid ${colors.SECONDARY_200}`,
          borderRadius: 12,
          padding: 0,
        },
        mask: { backgroundColor: 'rgba(0, 0, 0, 0.6)' },
      }}
    >
      <div
        css={css`
          padding: 24px;
        `}
      >
        {/* Header */}
        <h2
          css={css`
            margin: 0 0 8px;
            font-family: 'Inter', sans-serif;
            font-size: 14px;
            font-weight: 600;
            letter-spacing: 1.68px;
            text-transform: uppercase;
            color: ${colors.NEUTRAL_0};
          `}
        >
          Import Game Slots
        </h2>
        <p
          css={css`
            margin: 0 0 20px;
            font-family: 'Inter', sans-serif;
            font-size: 13px;
            color: ${colors.NEUTRAL_50};
          `}
        >
          Upload a CSV file with date-specific game slots. Download the template
          to see the expected format.
          <br />
          Date format: MM/DD/YYYY (e.g., 05/06/2026). YYYY-MM-DD also accepted.
        </p>

        {/* Template download */}
        <button
          type='button'
          onClick={handleTemplateDownload}
          disabled={templateLoading}
          css={css`
            display: flex;
            align-items: center;
            gap: 8px;
            padding: 8px 16px;
            margin-bottom: 20px;
            background: transparent;
            border: 1px solid ${colors.PRIMARY};
            border-radius: 8px;
            color: ${colors.PRIMARY};
            font-family: 'Inter', sans-serif;
            font-size: 13px;
            font-weight: 500;
            cursor: pointer;
            transition: all 0.2s ease;

            &:hover {
              background: ${colors.PRIMARY}20;
            }
            &:disabled {
              opacity: 0.5;
              cursor: not-allowed;
            }
          `}
        >
          <DownloadOutlined />
          {templateLoading ? 'Downloading...' : 'Download Template'}
        </button>

        {/* Result banner */}
        {result && (
          <div
            css={css`
              display: flex;
              align-items: flex-start;
              gap: 10px;
              padding: 12px 16px;
              margin-bottom: 16px;
              border-radius: 8px;
              background: ${result.type === 'success'
                ? `${colors.SUCCESS_300}15`
                : `${colors.ERROR_300}15`};
              border: 1px solid
                ${result.type === 'success'
                  ? colors.SUCCESS_300
                  : colors.ERROR_300};
            `}
          >
            {result.type === 'success' ? (
              <CheckCircleOutlined
                css={css`
                  color: ${colors.SUCCESS_300};
                  font-size: 16px;
                  margin-top: 2px;
                `}
              />
            ) : (
              <CloseCircleOutlined
                css={css`
                  color: ${colors.ERROR_300};
                  font-size: 16px;
                  margin-top: 2px;
                `}
              />
            )}
            <div>
              <div
                css={css`
                  font-family: 'Inter', sans-serif;
                  font-size: 13px;
                  font-weight: 600;
                  color: ${colors.NEUTRAL_0};
                  margin-bottom: 2px;
                `}
              >
                {result.title}
              </div>
              <div
                css={css`
                  font-family: 'Inter', sans-serif;
                  font-size: 12px;
                  color: ${colors.NEUTRAL_50};
                `}
              >
                {result.message}
              </div>
              {result.errors &&
                result.errors.length > 0 &&
                (() => {
                  // Deduplicate: group by row, collect unique messages
                  const grouped = new Map<string, Set<string>>()
                  let hasLocationOrFacilityError = false
                  for (const err of result.errors) {
                    const row = String(err.line || '?')
                    if (!grouped.has(row)) {
                      grouped.set(row, new Set())
                    }
                    if ('location' in err || 'facility' in err) {
                      hasLocationOrFacilityError = true
                    }
                    Object.entries(err)
                      .filter(([k]) => k !== 'line')
                      .forEach(([, v]) => grouped.get(row)!.add(v as string))
                  }
                  const rows = Array.from(grouped.entries())
                  const totalRows = rows.length
                  return (
                    <div
                      css={css`
                        margin-top: 8px;
                        max-height: 150px;
                        overflow-y: auto;
                      `}
                    >
                      {rows.slice(0, 10).map(([row, messages]) => (
                        <div
                          key={row}
                          css={css`
                            font-family: 'Inter', sans-serif;
                            font-size: 12px;
                            color: ${colors.ERROR_100};
                            margin-bottom: 4px;
                          `}
                        >
                          <strong>Row {row}:</strong>{' '}
                          {Array.from(messages).join('; ')}
                        </div>
                      ))}
                      {totalRows > 10 && (
                        <div
                          css={css`
                            font-family: 'Inter', sans-serif;
                            font-size: 12px;
                            color: ${colors.NEUTRAL_50};
                            margin-top: 4px;
                          `}
                        >
                          ...and {totalRows - 10} more rows with errors
                        </div>
                      )}
                      {hasLocationOrFacilityError && (
                        <div
                          css={css`
                            font-family: 'Inter', sans-serif;
                            font-size: 12px;
                            color: ${colors.NEUTRAL_50};
                            margin-top: 8px;
                            padding-top: 8px;
                            border-top: 1px solid ${colors.SECONDARY_200};
                          `}
                        >
                          To manage locations and facilities, go to the
                          Scheduler Admin tab and click &quot;Add
                          Location&quot;.
                        </div>
                      )}
                    </div>
                  )
                })()}
            </div>
          </div>
        )}

        {/* Drop zone */}
        <div
          onDragOver={(e) => {
            e.preventDefault()
            setIsDragOver(true)
          }}
          onDragLeave={() => setIsDragOver(false)}
          onDrop={handleDrop}
          onClick={() => fileInputRef.current?.click()}
          css={css`
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            padding: 32px;
            border: 2px dashed
              ${isDragOver ? colors.PRIMARY : colors.SECONDARY_100};
            border-radius: 8px;
            background: ${isDragOver
              ? `${colors.PRIMARY}10`
              : colors.SECONDARY_500};
            cursor: pointer;
            transition: all 0.2s ease;
            margin-bottom: 20px;

            &:hover {
              border-color: ${colors.PRIMARY};
              background: ${colors.PRIMARY}10;
            }
          `}
        >
          <InboxOutlined
            css={css`
              font-size: 36px;
              color: ${file ? colors.PRIMARY : colors.NEUTRAL_100};
              margin-bottom: 8px;
            `}
          />
          {file ? (
            <span
              css={css`
                font-family: 'Inter', sans-serif;
                font-size: 14px;
                color: ${colors.PRIMARY};
                font-weight: 500;
              `}
            >
              {file.name}
            </span>
          ) : (
            <>
              <span
                css={css`
                  font-family: 'Inter', sans-serif;
                  font-size: 14px;
                  color: ${colors.NEUTRAL_50};
                  margin-bottom: 4px;
                `}
              >
                Drag & drop a file here, or click to browse
              </span>
              <span
                css={css`
                  font-family: 'Inter', sans-serif;
                  font-size: 12px;
                  color: ${colors.NEUTRAL_100};
                `}
              >
                Accepts .csv files
              </span>
            </>
          )}
          <input
            ref={fileInputRef}
            type='file'
            accept='.csv'
            onChange={(e) => {
              const selected = e.target.files?.[0]
              if (selected) {
                handleFileSelect(selected)
              }
            }}
            css={css`
              display: none;
            `}
          />
        </div>

        {/* Action buttons */}
        <div
          css={css`
            display: flex;
            justify-content: flex-end;
            gap: 12px;
          `}
        >
          <button
            type='button'
            onClick={handleClose}
            css={css`
              padding: 8px 20px;
              background: transparent;
              border: 1px solid ${colors.SECONDARY_100};
              border-radius: 8px;
              color: ${colors.NEUTRAL_0};
              font-family: 'Inter', sans-serif;
              font-size: 14px;
              cursor: pointer;
              transition: all 0.2s ease;

              &:hover {
                background: ${colors.SECONDARY_300};
              }
            `}
          >
            {result?.type === 'success' ? 'Done' : 'Cancel'}
          </button>
          {(!result || result.type === 'error') && (
            <button
              type='button'
              onClick={handleUpload}
              disabled={!file || loading}
              css={css`
                display: flex;
                align-items: center;
                gap: 8px;
                padding: 8px 20px;
                background: ${colors.PRIMARY};
                border: none;
                border-radius: 8px;
                color: ${colors.PRIMARY_TEXT};
                font-family: 'Inter', sans-serif;
                font-size: 14px;
                font-weight: 600;
                cursor: pointer;
                transition: all 0.2s ease;

                &:hover {
                  background: ${colors.PRIMARY_HOVER};
                }
                &:disabled {
                  opacity: 0.4;
                  cursor: not-allowed;
                }
              `}
            >
              <UploadOutlined />
              {loading ? 'Importing...' : 'Import'}
            </button>
          )}
        </div>
      </div>
    </Modal>
  )
}

export default ConcreteSlotImportModal
